import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { ConfigurationService } from "@core/configuration.service";
import * as AbstractionTariff from "@helper/abstraction/tariff";
import * as AbstractionForeignPartners from "@helper/abstraction/foreign-partners";
import * as AbstractionForeignStorages from "@helper/abstraction/storages";
import { FilterRequestParams, PartnersFilterDto, PartyDepartmentFilter } from "@helper/abstraction/filter";
import { PartnersParams, PartnersDto, ForeignPartnerDto } from "@helper/abstraction/partners";
import { UnloadingPointsParams, UnloadingPointsDto } from "@helper/abstraction/unload-points";
import { ConsigneesParams, ConsigneesDto, Consignee } from "@helper/abstraction/consignee";
import { LoadingPointsParams, LoadingPointsDto } from "@helper/abstraction/loading-points";
import { PartyDto, PrividersListParams } from "@helper/abstraction/mns-ewaybill";
import * as AbstractOrganization from "@helper/abstraction/organization";
import { isEveryFieldNull } from "@helper/methods";
import { ClientFunctionDto } from "@helper/abstraction/client-functions";
import { FileBase64DtoUI } from "./export";

export class Organization {
	public partners = {
		list: {
			post$: (pp: FilterRequestParams): Observable<PartnersDto> => {
				const url = `${this.config.api.root}/organization/partners/list`;
				let params = new HttpParams();
				if (pp.page) {
					params = params.set("page", pp.page.toString());
				}
				if (pp.size) {
					params = params.set("size", pp.size.toString());
				}
				let value: PartnersFilterDto = {
					name: pp.search || pp.name,
					gln: pp?.gln,
					unp: pp?.unp,
					roseuUuid: pp?.roseuUuid,
					kpp: pp?.kpp,
				};
				if (pp.connectorGln) {
					value = { ...value, connectorGln: pp.connectorGln };
				}
				if (pp.status) {
					value = { ...value, status: pp.status };
				}
				if (pp.kpp) {
					value = { ...value, kpp: pp.kpp };
				}
				if (pp.roseuUuid) {
					value = { ...value, roseuUuid: pp.roseuUuid };
				}

				for (let k in value) {
					if ([null, undefined, ""].includes((value as any)[k])) {
						delete (value as any)[k];
					}
				}
				return this.http.post<PartnersDto>(url, !isEveryFieldNull(value) ? value : {}, { params, withCredentials: true });
			}
		},

		foreign: {
			foreignAdd$: (foreign: ForeignPartnerDto): Observable<ForeignPartnerDto> => {
				const url = `${this.config.api.root}/organization/addForeignPartner`;
				return this.http.post<ForeignPartnerDto>(url, foreign, { withCredentials: true });
			},
			foreignGet$: (id: number): Observable<ForeignPartnerDto> => {
				const url = `${this.config.api.root}/organization/getForeignPartner/${id}`;
				return this.http.get<ForeignPartnerDto>(url, { withCredentials: true });
			},
			foreignEdit$: (foreign: ForeignPartnerDto): Observable<ForeignPartnerDto> => {
				const url = `${this.config.api.root}/organization/editForeignPartner`;
				return this.http.post<ForeignPartnerDto>(url, foreign, { withCredentials: true });
			},
		},

		foreignPartnerRequest: {
			foreignFilteredList$: (filter: AbstractionForeignPartners.ForeignPartnerRequestListFilterDto, filterParams: { page?: number, size?: number }): Observable<AbstractionForeignPartners.ForeignPartnerPageDto> => {
				const url = `${this.config.api.root}/foreignPartnerRequest/filteredList`;

				let params = new HttpParams();
				params = params.set("page", filterParams.page?.toString() || "1");
				params = params.set("size", filterParams.size?.toString() || "50");
				return this.http.post<AbstractionForeignPartners.ForeignPartnerPageDto>(url, filter || {}, { params, withCredentials: true });
			},
			create$: (data: AbstractionForeignPartners.ForeignPartnerRequestDto): Observable<any> => {
				const url = `${this.config.api.root}/foreignPartnerRequest`;
				return this.http.post<any>(url, data, { withCredentials: true });
			},
			cancel$: (id: number): Observable<any> => {
				const url = `${this.config.api.root}/foreignPartnerRequest/${id}/cancel`;
				return this.http.post<any>(url, null, { withCredentials: true });
			},
		}
	};

	public suppliers = {
		list: {
			get$: (sup: ConsigneesParams): Observable<ConsigneesDto> => {
				const url = `${this.config.api.root}/organization/suppliers/list`;
				let params = new HttpParams();
				params = params.set("documentTypeId", sup.documentTypeId);
				params = params.set("page", sup.page.toString());
				params = params.set("size", sup.size.toString());

				if (sup.searchText) {
					params = params.set("supplierName", sup.searchText);
				}

				return this.http.get<ConsigneesDto>(url, { params, withCredentials: true });
			}
		}
	};

	public buyers = {
		list: {
			get$: (pp: PartnersParams): Observable<PartnersDto> => {
				const url = `${this.config.api.root}/organization/buyers/list`;
				let params = new HttpParams();
				params = params.set("page", pp.page.toString());
				params = params.set("size", pp.size.toString());
				if (pp.documentTypeId)
					params = params.set("documentTypeId", pp.documentTypeId);
				if (pp.searchText)
					params = params.set("buyerName", pp.searchText);
				return this.http.get<PartnersDto>(url, { params, withCredentials: true });
			}
		}
	};

	public storages = {
		list: {
			get$: (sp: AbstractionForeignStorages.StoragesParams): Observable<AbstractionForeignStorages.StoragesDto> => {
				const url = `${this.config.api.root}/organization/storages/list`;
				let params = new HttpParams();
				params = params.set("page", sp.page.toString());
				params = params.set("size", sp.size.toString());
				if (sp.searchText)
					params = params.set("storageName", sp.searchText);
				return this.http.get<AbstractionForeignStorages.StoragesDto>(url, { params, withCredentials: true });
			},
			getByStorageType$: (sp: AbstractionForeignStorages.TypedStoragesParams): Observable<AbstractionForeignStorages.StoragesDto> => {
				const url = `${this.config.api.root}/organization/storages/${sp.storageTypeId}/list`;
				let params = new HttpParams();
				params = params.set("page", sp.page.toString());
				params = params.set("size", sp.size.toString());

				if (sp.documentTypeId) {
					params = params.set("documentTypeId", sp.documentTypeId);
				}
				if (sp.search) {
					params = params.set("storageName", sp.search);
				}
				if (sp.partnerId) {
					params = params.set("partnerId", sp.partnerId.toString());
				}
				return this.http.get<AbstractionForeignStorages.StoragesDto>(url, { params, withCredentials: true });
			},
			loadingPoints$: (lpp: LoadingPointsParams): Observable<LoadingPointsDto> => {
				const url = `${this.config.api.root}/organization/storages/loading-points/list`;
				let params = new HttpParams();
				params = params.set("page", lpp.page.toString());
				params = params.set("size", lpp.size.toString());
				if (lpp.searchText)
					params = params.set("storageName", lpp.searchText);
				return this.http.get<LoadingPointsDto>(url, { params, withCredentials: true });
			}
		},
		get$: (storageId: number): Observable<AbstractionForeignStorages.Storage> => {
			const url = `${this.config.api.root}/organization/storages/${storageId}`;
			return this.http.get<AbstractionForeignStorages.Storage>(url, { withCredentials: true });
		},
		partners: {
			get$: (relatedPartyId: number, queryParams: FilterRequestParams): Observable<AbstractionForeignStorages.Storage> => {
				const url = `${this.config.api.root}/organization/partners/storages/filteredPage`;
				let params = new HttpParams();
				params = params.set("page", queryParams.page.toString());
				params = params.set("size", queryParams.size.toString());
				params = params.set("relatedPartyId", relatedPartyId.toString());

				if (queryParams.searchString) {
					params = params.set("searchString", queryParams.searchString.toString());
				}
				return this.http.get<AbstractionForeignStorages.Storage>(url, { params, withCredentials: true });
			}
		}
	};

	public rates_and_billing = {
		list: {
			get$: (): Observable<any> => {
				const url = `${this.config.api.root}/organization/tariff`;
				return this.http.get<ConsigneesDto>(url, { withCredentials: true });
			}
		}
	};

	public public_contract = {
		list: {
			get$: (filter: FilterRequestParams): Observable<any> => {
				const url = `${this.config.api.root}/organization/publicContract/list`;
				const params = new HttpParams()
					.set("page", filter.page.toString())
					.set("size", filter.size.toString());
				return this.http.get<AbstractOrganization.PublicContractsList>(url, { params, withCredentials: true });
			}
		},
		accept: {
			post$: (code: string): Observable<void> => {
				const url = `${this.config.api.root}/organization/publicContract/${code}/accept`;
				return this.http.post<void>(url, null, { withCredentials: true });
			}
		}
	};

	public application = {
		page: {
			get$: (): Observable<AbstractOrganization.ParticipantStatementList> => {
				const url = `${this.config.api.root}/organization/application/page`;
				return this.http.get<AbstractOrganization.ParticipantStatementList>(url, { withCredentials: true });
			}
		},
		send: {
			post$: (): Observable<void> => {
				const url = `${this.config.api.root}/organization/application/send`;
				return this.http.post<void>(url, null, { withCredentials: true });
			}
		},
		file: {
			get$: (itemId: number): Observable<FileBase64DtoUI> => {
				const url = `${this.config.api.root}/organization/application/${itemId}/file/base64`;
				return this.http.get<FileBase64DtoUI>(url, { withCredentials: true });
			}
		}
	};

	public partyDepartment = {
		get$: (filter: PartyDepartmentFilter): Observable<AbstractOrganization.PartyDepartmentList> => {
			const url = `${this.config.api.root}/organization/partyDepartment`;
			let params = new HttpParams();
			if (filter?.name) {
				params = params.set("name", filter?.name);
			}
			if (filter?.id) {
				params = params.set("id", filter?.id);
			}
			return this.http.get<AbstractOrganization.PartyDepartmentList>(url, { params, withCredentials: true });
		},
		post$: (partyDepartment: AbstractOrganization.PartyDepartmentDto): Observable<AbstractOrganization.PartyDepartmentDto> => {
			const url = `${this.config.api.root}/organization/partyDepartment`;
			return this.http.post<AbstractOrganization.PartyDepartmentDto>(url, partyDepartment, { withCredentials: true });
		},
		delete$: (id: number): Observable<void> => {
			const url = `${this.config.api.root}/organization/partyDepartment/${id}`;
			return this.http.delete<void>(url, { body: id, withCredentials: true });
		},
	};

	public consignees = {
		list: {
			get$: (cp: ConsigneesParams, isVersion = true, emptyList = false): Observable<ConsigneesDto> => {
				const url = `${this.config.api.root}/organization/consignees/list`;
				let params = new HttpParams();
				params = params.set("documentTypeId", cp.documentTypeId);
				params = params.set("page", cp.page.toString());
				params = params.set("size", cp.size.toString());

				if (cp.searchText) {
					params = params.set("consigneeName", cp.searchText);
				}

				if (typeof cp.isForeignPartner === "boolean" && isVersion) {
					params = params.set("isForeignPartner", cp.isForeignPartner);
				}

				if (typeof cp.internal === "boolean" && isVersion) {
					params = params.set("internal", cp.internal);
				}

				return this.http.get<ConsigneesDto>(url, { params, withCredentials: true }).pipe(map((el: any) => {
					const list = el.map((item: Consignee) => {
							return {
									...item,
									fullName: item?.organizationType === "INDIVIDUAL_ENTREPRENEUR"
										? item?.fullNameIndividual
										: item.fullName
									};
						});
					
					return emptyList ? [] : list; }));
			},

			unloadingPoints$: (upp: UnloadingPointsParams): Observable<UnloadingPointsDto> => {
				const url = `${this.config.api.root}/organization/consignees/${upp.consigneeId}/storages/unloading-points/list`;
				let params = new HttpParams();
				params = params.set("page", upp.page.toString());
				params = params.set("size", upp.size.toString());
				params = params.set("documentTypeId", upp.documentTypeId.toString());

				if (upp.searchText) {
					params = params.set("consigneeName", upp.searchText);
				}

				return this.http.get<UnloadingPointsDto>(url, { params, withCredentials: true });
			}
		}
	};

	public tariff = {
		plan: {
			current$: (): Observable<AbstractionTariff.TariffPlanDto> => {
				const url = `${this.config.api.root}/tariff/plan/current`;
				return this.http.get<AbstractionTariff.TariffPlanDto>(url, { withCredentials: true });
			},
			allPackage$: (planId: number): Observable<AbstractionTariff.TariffPackageDto[]> => {
				const url = `${this.config.api.root}/tariff/plan/${planId}/package`;
				return this.http.get<AbstractionTariff.TariffPackageDto[]>(url, { withCredentials: true });
			},
			status$: (planId: number, status: string): Observable<AbstractionTariff.TariffPackageDto[]> => {
				const url = `${this.config.api.root}/tariff/plan/${planId}/package/${status}`;
				return this.http.get<AbstractionTariff.TariffPackageDto[]>(url, { withCredentials: true });
			},
			archive$: (): Observable<AbstractionTariff.ArchiveTariffPlanDto> => {
				const url = `${this.config.api.root}/tariff/plan`;
				return this.http.get<AbstractionTariff.ArchiveTariffPlanDto>(url, { withCredentials: true });
			},
		},
		package: {
			function$: (packageId: number): Observable<AbstractionTariff.TariffFunctionDto[]> => {
				const url = `${this.config.api.root}/tariff/package/${packageId}/function`;
				return this.http.get<AbstractionTariff.TariffFunctionDto[]>(url, { withCredentials: true });
			}
		},
		paidList: {
			paid$: (): Observable<AbstractionTariff.TariffPlanPaidListDto> => {
				const url = `${this.config.api.root}/tariff/plan/paid?page=1&size=50`;
				return this.http.get<AbstractionTariff.TariffPlanPaidListDto>(url, { withCredentials: true });
			}
		},
		bill: {
			post$: (itemId: number): Observable<HttpResponse<Blob>> => {
				const url = `${this.config.api.root}/tariff/bill/${itemId}`;
				return this.http.post<Blob>(url, {}, {
					headers: new HttpHeaders({ "Content-Type": "application/json" }),
					withCredentials: true,
					responseType: "blob" as "json",
					observe: "response",
				});
			}
		}
	}

	public providers = {
		list: {
			get$: (data?: PrividersListParams): Observable<PartyDto[]> => {
				const url = `${this.config.api.root}/organization/providers/list`;
				let params = new HttpParams();

				if (typeof data?.autoRoamingProvider === "boolean") {
					params = params.set("autoRoamingProvider", data.autoRoamingProvider);
				}
				if (data?.countryId) {
					params = params.set("countryId", data.countryId);
				}
				return this.http.get<PartyDto[]>(url, { withCredentials: true, params });
			}
		},
		infoFromProviders: {
			get$: (data: AbstractOrganization.InfoFromProvidersParams): Observable<AbstractOrganization.PartyInfoInProviderListDto> => {
				const url = `${this.config.api.root}/organization/infoFromProviders/${data.tab}`;
				let params = new HttpParams();

				if (data.inn) {
					params = params.set("inn", data.inn);
				}
				if (data.kpp) {
					params = params.set("kpp", data.kpp);
				}
				if (data.roseuUUID) {
					params = params.set("roseuUUID", data.roseuUUID);
				}
				return this.http.get<AbstractOrganization.PartyInfoInProviderListDto>(url, { withCredentials: true, params });
			}
		}
	};

	public createStorage = {
		create: {
			post$: (partyId: number, storageGln: string): Observable<AbstractionForeignStorages.StorageCreateDto> => {
				const url = `${this.config.api.root}/organization/consignees/${partyId}/storage`;
				let params = new HttpParams();
				params = params.set("storageGln", storageGln);
				return this.http.post<AbstractionForeignStorages.StorageCreateDto>(url, null, { params, withCredentials: true });
			}
		}
	};

	public checkGln = {
		check: {
			get$: (gln: string): Observable<AbstractOrganization.PartyCheckDto> => {
				const url = `${this.config.api.root}/organization/checkGln`;
				let params = new HttpParams();
				params = params.set("gln", gln);
				return this.http.get<AbstractOrganization.PartyCheckDto>(url, { params, withCredentials: true });
			}
		}
	};

	public updateGln = {
		update: {
			post$: (partyCheckDto: AbstractOrganization.PartyCheckDto): Observable<AbstractOrganization.PartyCheckDto> => {
				const url = `${this.config.api.root}/organization/updateGln`;
				const partySend = partyCheckDto;
				partySend.warningPresent && delete partySend.warningPresent;
				partySend.warningText && delete partySend.warningText;
				return this.http.post<AbstractOrganization.PartyCheckDto>(url, partySend, { withCredentials: true });
			}
		}
	};

	public exportXLSXDoc = {
		get$: (): Observable<HttpResponse<Blob>> => {
			const url = `${this.config.api.root}/organization/partners/storages/upload`;
			return this.http.get<Blob>(url, {
				withCredentials: true,
				responseType: "blob" as "json",
				observe: "response",
			});
		}
	}

	public transinet = {
		registration: {
			post$: (registrationInfo: AbstractOrganization.TransinetPartyRegistrationDto): Observable<void> => {
				const url = `${this.config.api.root}/organization/transinet/registration`;
				return this.http.post<void>(url, registrationInfo, { withCredentials: true });
			},
		}
	}

	public clientFunctions = {
		post$: (clientFunctionDtoList: ClientFunctionDto[]): Observable<ClientFunctionDto[]> => {
			const url = `${this.config.api.root}/organization/clientFunctions`;
			return this.http.post<ClientFunctionDto[]>(url, clientFunctionDtoList, { withCredentials: true });
		}
	};

	constructor(private config: ConfigurationService, private http: HttpClient) { }

	public get$(): Observable<AbstractOrganization.Organization> {
		const url = `${this.config.api.root}/organization`;
		return this.http.get<AbstractOrganization.OrganizationDto>(url, { withCredentials: true }).pipe(
			map(data => {
				data.dateUpdate = new Date(data.dateUpdate);
				data.integrationTokenExpireDate = data?.integrationTokenExpireDate ? new Date(data?.integrationTokenExpireDate).toISOString() : undefined;
				return data;
			}));
	}

	public post$(data: AbstractOrganization.Organization): Observable<void> {
		const url = `${this.config.api.root}/organization`;
		return this.http.post<void>(url, data, { withCredentials: true });
	}
}
